import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { AnalyticsSkuViewed } from '@repo/segment/Analytics/AnalyticsSkuViewed';
import { SEGMENT_SKU_VIEWED_SOURCE } from '@repo/segment/Analytics/constants';
import { MonetizableEnum } from '@repo/segment/enums';
import type { ProductCard as ProductCardType } from '@repo/supergraph-schemas/roundup/base/types';
import { CtaButton } from '../../../../CtaButton';
import { StarRatingAboveTooltipBelowLayout } from '../../../../StarRating/StarRatingAboveTooltipBelowLayout';
import { CtaName } from '../../../components/CtaName';
import { AddToCompareCheckbox } from './AddToCompareCheckbox';
import { Drivers } from './Drivers';
import { NerdwalletTake } from './NerdwalletTake';
import { ProductImage } from './ProductImage';
import { ProductImageWrapper } from './ProductImageWrapper';

export const ProductCard = ({
  productCard,
  index = 0,
  isCompareEnabled = false,
}: {
  productCard: ProductCardType;
  index: number;
  isCompareEnabled?: boolean;
}) => {
  const {
    marketplaceEntity: {
      name,
      productType,
      institution,
      product,
      id,
      monetizable,
      image,
      starRating,
      starRatingTooltip,
      cdDepositTerm,
      displayName,
      cta,
      federallyInsuredAnnotation,
      driverApy,
      driverMinimumBalance,
      reviewLink,
      prosBullets,
      consBullets,
    },
    bottomLine,
    pros,
    cons,
  } = productCard;

  const ctaAnalytics = {
    index,
    institutionId: institution?.id,
    monetizable: monetizable || MonetizableEnum.no,
    offerId: id,
    productId: product?.id,
    productType,
  };

  return (
    <AnalyticsSkuViewed
      source={SEGMENT_SKU_VIEWED_SOURCE}
      name={name}
      productType={productType}
      level="offer"
      institutionId={institution?.id}
      institutionName={institution?.name}
      productId={product?.id}
      productName={product?.name}
      offerId={id}
      offerName={name}
      monetizable={monetizable}
      impressionSection="banking_cd_filter_section"
      impressionPosition={index}
      additionalAttributes={{
        page_type: 'roundup',
      }}
    >
      <div style={{ width: '100%' }}>
        <Grid
          container
          sx={{
            display: 'grid',
            gridTemplateAreas: {
              xs: isCompareEnabled
                ? `
                    "   title              title  "
                    "   image          starRating "
                    "   image           compare   "
                    "    cta              cta     "
                    "      .               .      "
                    "   drivers         drivers   "
                  `
                : `
                    "   title              title  "
                    "   image          starRating "
                    "    cta              cta     "
                    "      .               .      "
                    "   drivers         drivers   "
                  `,
              sm: isCompareEnabled
                ? `
                    "   image              .           title         compare    "
                    "   image              .            .              .        "
                    "   image              .          drivers         cta       "
                    "  starRating          .          drivers         cta       "
                  `
                : `
                    "   image              .           title         title      "
                    "   image              .          drivers         cta       "
                    "  starRating          .          drivers         cta       "
                  `,
            },
            gridTemplateColumns: {
              xs: 'auto auto',
              sm: 'auto 32px auto auto',
            },
            gridTemplateRows: {
              xs: isCompareEnabled ? 'auto auto auto auto 12px auto' : 'auto auto auto 12px auto',
              sm: isCompareEnabled ? 'auto auto auto' : '32px auto auto',
            },
            p: 3,
            pb: 0,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            border: '1px solid',
            borderColor: 'neutral.lighter',
            maxWidth: '1152px',
          }}
        >
          <Grid
            sx={{
              gridArea: 'image',
              display: 'flex',
              justifyContent: 'center',
              pb: { xs: '12px', sm: 0 },
            }}
          >
            {image?.source && (
              <ProductImageWrapper marketplaceEntity={productCard.marketplaceEntity} index={index}>
                <ProductImage source={image.source} alt={image.alt} />
              </ProductImageWrapper>
            )}
          </Grid>
          <Grid
            sx={{
              gridArea: 'starRating',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minWidth: '137px',
              pb: { xs: isCompareEnabled ? 2 : '12px', sm: 0 },
            }}
          >
            <Box sx={{ pb: { sm: 3 }, width: { sm: 120 } }}>
              <StarRatingAboveTooltipBelowLayout
                starRating={starRating}
                starRatingTooltip={starRatingTooltip}
                reviewLink={reviewLink}
                align="center"
              />
            </Box>
          </Grid>
          <Grid sx={{ gridArea: 'title', pb: { xs: 3, sm: 0 } }}>
            {cta ? (
              <CtaName
                cta={cta}
                ctaAnalytics={ctaAnalytics}
                displayName={displayName}
                sx={{ fontWeight: 600, fontSize: 14 }}
              />
            ) : (
              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{displayName}</Typography>
            )}
          </Grid>
          <Grid
            sx={{
              gridArea: 'drivers',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              maxWidth: { md: '626px' },
              minWidth: { xs: '307px', md: '350px' },
              pb: 3,
            }}
          >
            <Drivers
              driverApy={driverApy}
              driverMinimumBalance={driverMinimumBalance}
              cdDepositTerm={cdDepositTerm}
            />
          </Grid>
          <Grid sx={{ gridArea: 'cta', pb: { sm: 3 }, justifyItems: 'center' }}>
            <Box sx={{ width: { xs: '100%', sm: 180 } }}>
              <CtaButton
                cta={cta}
                analyticsPayload={ctaAnalytics}
                federallyInsuredAnnotation={federallyInsuredAnnotation}
                useCdRatesStyle
              />
            </Box>
          </Grid>
          {isCompareEnabled && (
            <Grid
              sx={{
                gridArea: 'compare',
                justifyItems: { sm: 'center' },
                pb: 3,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  width: { xs: '100%', sm: 180 },
                }}
              >
                <AddToCompareCheckbox offer={productCard} index={index} />
              </Box>
            </Grid>
          )}
        </Grid>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'neutral.lighter',
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
            borderTop: 0,
            maxWidth: '1152px',
            px: 3,
          }}
        >
          <NerdwalletTake
            content={bottomLine}
            reviewLink={reviewLink}
            pros={pros?.length ? pros : prosBullets}
            cons={cons?.length ? cons : consBullets}
            id={id}
            index={index}
          />
        </Box>
      </div>
    </AnalyticsSkuViewed>
  );
};
